import { useState, useEffect } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import PatientDataTable from '../../components/admin/PatientDataTable';
import FilterPanel from '../../components/admin/FilterPanel';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  getPatients,
  exportData,
  downloadBlob,
  getChatLogSource,
  setChatLogSource,
  deletePatientConversations,
} from '../../lib/admin-api';
import type {
  PatientSummary,
  PaginatedResult,
  ListFilters,
  SortField,
  SortOrder,
  ChatLogSourceStatus,
} from '../../types/admin';
import {
  Download,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Users,
  MessageSquare,
  Calendar,
  Database,
} from 'lucide-react';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Card, CardContent } from '../../components/ui/card';

export default function AdminDashboard() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [data, setData] = useState<PaginatedResult<PatientSummary> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Conversation source (database vs S3) and patient deletion
  const [source, setSource] = useState<ChatLogSourceStatus | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Pagination and sorting
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [sortBy, setSortBy] = useState<SortField>('lastChatTime');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Filters
  const [filters, setFilters] = useState<ListFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<ListFilters>({});

  const fetchPatients = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getPatients(page, limit, sortBy, sortOrder, appliedFilters);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load patients');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPatients();
    }
  }, [isAuthenticated, page, sortBy, sortOrder, appliedFilters]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setSourceLoading(true);
    getChatLogSource()
      .then(setSource)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load conversation source'))
      .finally(() => setSourceLoading(false));
  }, [isAuthenticated]);

  const handleSourceChange = async (useS3: boolean) => {
    setSourceLoading(true);
    setError(null);
    try {
      const result = await setChatLogSource(useS3 ? 's3' : 'database');
      setSource((prev) => (prev ? { ...prev, source: result.source } : prev));
      setPage(1);
      await fetchPatients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change conversation source');
    } finally {
      setSourceLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deletePatientConversations(deleteTarget);
      await fetchPatients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete chat history');
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
    setPage(1);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    setIsExporting(true);
    try {
      const blob = await exportData(format, undefined, appliedFilters);
      const filename = `chat-export-${new Date().toISOString().split('T')[0]}.${format}`;
      downloadBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="rounded-full bg-blue-100 p-3">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Patients</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data?.total ?? '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="rounded-full bg-green-100 p-3">
                <MessageSquare className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Sessions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data?.items.reduce((sum, p) => sum + p.sessionCount, 0) ?? '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-4">
              <div className="rounded-full bg-purple-100 p-3">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Showing</p>
                <p className="text-2xl font-bold text-gray-900">
                  Page {data?.page ?? 1} of {data?.totalPages ?? 1}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Conversation source */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Database className="h-5 w-5 text-gray-400" />
            <Switch
              id="chat-log-source"
              checked={source?.source === 's3'}
              disabled={sourceLoading || !source?.s3Available}
              onCheckedChange={handleSourceChange}
              aria-label="Read conversations from S3"
            />
            <Label htmlFor="chat-log-source" className="cursor-pointer">
              Read conversations from S3
            </Label>
            {sourceLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <span className="text-xs text-gray-400">
              Current: {source ? source.source : '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2 ml-9">
            {source?.s3Available
              ? 'Off reads the application database, where patients appear by FHIR ID only. On reads the S3 transcript bucket.'
              : 'S3 is not configured on this deployment. Conversations are read from the application database, where patients appear by FHIR ID only.'}
          </p>
        </CardContent>
      </Card>

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      {/* Export Button */}
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExport('csv')}
          disabled={isExporting}
          title="Export all conversations to CSV"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export CSV
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <PatientDataTable
        patients={data?.items ?? []}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onDelete={source?.source === 'database' ? setDeleteTarget : undefined}
        isLoading={isLoading}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this patient's chat history?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes every conversation for patient{' '}
              <span className="font-mono break-all">{deleteTarget}</span> from the application database.
              Export first if you need a copy. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete history'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-600">
            Showing {(page - 1) * limit + 1} to{' '}
            {Math.min(page * limit, data.total)} of {data.total} patients
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= data.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
